import React, { useState, useEffect } from 'react'
import { Box, Button, Group, TextInput, Select, MultiSelect, LoadingOverlay, Text, Avatar, NumberInput, Divider, Grid } from '@mantine/core'
import { getAllCountryList } from '../../../../services/location'
import { getCategoryRules } from '../../../../services/category'
import { createHscode, updateHscode, getDetailHscode } from '../../../../services/hscodes'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'

const FormHscode = ({ dataDetail, onCloseModal, reloadList }) => {
    const [loading, setLoading] = useState(false)

    // Dropdown Data States
    const [loadingCountries, setLoadingCountries] = useState(false)
    const [countryList, setCountryList] = useState([])
    const [loadingRules, setLoadingRules] = useState(false)
    const [ruleList, setRuleList] = useState([])

    // Form state
    const [form, setForm] = useState({
        country_id: null,
        native_language: null,
        native_language_country_id: null,
        code: '',
        description: '',
        native_description: '',
        bm: 0,
        ppn: 0,
        pph: 0,
        rule_ids: []
    })

    // Validation state
    const [errors, setErrors] = useState({})

    useEffect(() => {
        fetchCountries()
        fetchRules()
    }, [])

    useEffect(() => {
        if (dataDetail?.id && countryList.length > 0) {
            handleFetchDetail(dataDetail.id)
        }
    }, [dataDetail?.id, countryList])

    const handleFetchDetail = async (id) => {
        setLoading(true)
        try {
            const response = await getDetailHscode(id, { include: 'categoryRules' })
            if (response && response.data) {
                const data = response.data
                const attr = data.attributes || data

                const bm = attr.taxes?.find(t => t.label.toLowerCase() === 'bm')?.value || 0
                const ppn = attr.taxes?.find(t => t.label.toLowerCase() === 'ppn')?.value || 0
                const pph = attr.taxes?.find(t => t.label.toLowerCase() === 'pph')?.value || 0
                const country_native_language = countryList.find(c => c.iso_code_2?.toLowerCase() === attr.native_language?.toLowerCase())
                let ruleIds = []
                const rulesData = data.relationships?.categoryRules

                if (rulesData && Array.isArray(rulesData)) {
                    ruleIds = rulesData.map(r => String(r.id))
                }
                setForm({
                    country_id: attr.country_id ? String(attr.country_id) : null,
                    native_language: attr.native_language || '',
                    native_language_country_id: country_native_language?.value || null,
                    code: attr.code || '',
                    description: attr.description || '',
                    native_description: attr.native_description || '',
                    bm: bm,
                    ppn: ppn,
                    pph: pph,
                    rule_ids: ruleIds
                })
            }
        } catch (error) {
            console.error("Failed to fetch detail", error)
            notifications.show({ title: 'Error', message: 'Failed to load details', color: 'red' })
        } finally {
            setLoading(false)
        }
    }

    const fetchCountries = async () => {
        setLoadingCountries(true)
        try {
            const response = await getAllCountryList({ 'page[limit]': 300 })
            if (response && response.data) {
                const mappedCountries = response.data.map(c => ({
                    value: String(c.attributes.id),
                    label: c.attributes.name,
                    iso_code_2: c.attributes.iso_code_2
                }))
                setCountryList(mappedCountries)
            }
        } catch (error) {
            console.error("Failed to fetch countries", error)
            notifications.show({ title: 'Error', message: 'Failed to load countries', color: 'red' })
        } finally {
            setLoadingCountries(false)
        }
    }

    const fetchRules = async () => {
        setLoadingRules(true)
        try {
            const response = await getCategoryRules({ 'page[num]': 1, 'page[limit]': 1000 })
            if (response && response.data) {
                const mappedRules = response.data.map(r => ({
                    value: String(r.id),
                    label: r.attributes.description || `Rule ${r.id}`
                }))
                setRuleList(mappedRules)
            }
        } catch (error) {
            console.error("Failed to fetch rules", error)
            notifications.show({ title: 'Error', message: 'Failed to load category rules', color: 'red' })
        } finally {
            setLoadingRules(false)
        }
    }

    const handleChange = (name, val) => {
        setForm(prev => {
            const newState = { ...prev, [name]: val }

            // Auto-fill native language from country iso code
            if (name === 'country_id') {
                const selectedCountry = countryList.find(c => c.value === val)
                if (selectedCountry?.iso_code_2) {
                    newState.native_language = selectedCountry.iso_code_2
                    newState.native_language_country_id = val
                }
            }

            if (name === 'native_language_country_id') {
                const selectedCountry = countryList.find(c => c.value === val)
                newState.native_language = selectedCountry?.iso_code_2 || ''
            }

            return newState
        })

        if (val || val === 0) {
            setErrors(prev => {
                const newErrors = { ...prev, [name]: '' }
                if (name === 'country_id') {
                    newErrors.native_language = ''
                    newErrors.native_language_country_id = ''
                }
                if (name === 'native_language_country_id') {
                    newErrors.native_language = ''
                }
                return newErrors
            })
        }
    }

    const validate = () => {
        let isValid = true
        const newErrors = {}

        if (!form.country_id) { newErrors.country_id = 'Country is required'; isValid = false }
        if (!form.code) { newErrors.code = 'HS Code is required'; isValid = false }

        setErrors(newErrors)
        return isValid
    }

    const handleSubmit = async () => {
        if (!validate()) return

        setLoading(true)
        try {
            const totalTax = Number(form.bm) + Number(form.ppn) + Number(form.pph)

            const payload = {
                country_id: parseInt(form.country_id),
                native_language: form.native_language,
                code: form.code,
                description: form.description,
                native_description: form.native_description,
                taxes: [
                    { label: "bm", value: Number(form.bm) },
                    { label: "ppn", value: Number(form.ppn) },
                    { label: "pph", value: Number(form.pph) }
                ],
                total_tax: totalTax,
                relationships: {
                    rule: [
                        {
                            action: "attach",
                            id: form.rule_ids.map(id => parseInt(id)),
                            type: "category_rule"
                        }
                    ]
                }
            }

            let response
            if (dataDetail) {
                response = await updateHscode(dataDetail.id, payload)
            } else {
                response = await createHscode(payload)
            }

            if (response.success || response.data) {
                notifications.show({
                    title: 'Success',
                    message: dataDetail ? 'HS Code updated successfully' : 'HS Code created successfully',
                    color: 'teal',
                    icon: <IconCheck />,
                })
                reloadList()
                onCloseModal()
            } else {
                throw new Error(response.message || 'Operation failed')
            }

        } catch (error) {
            console.error(error)
            notifications.show({
                title: 'Error',
                message: error.message || 'Internal Server Error',
                color: 'red',
                icon: <IconX />,
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box pos="relative">
            <LoadingOverlay visible={loading} overlayProps={{ radius: "sm", blur: 2 }} />

            <Box mb="md">
                <Select
                    label="Country"
                    placeholder="Select Country"
                    data={countryList || []}
                    value={form.country_id}
                    onChange={(val) => handleChange('country_id', val)}
                    disabled={loadingCountries}
                    searchable
                    withAsterisk
                    leftSection={
                        (() => {
                            const selected = (countryList || []).find(c => c.value === form.country_id)
                            return selected?.iso_code_2 ? (
                                <Avatar src={`https://flagcdn.com/w40/${selected.iso_code_2.toLowerCase()}.png`} size="xs" radius="xl" />
                            ) : null
                        })()
                    }
                    renderOption={({ option, checked }) => {
                        const country = countryList.find(c => c.value === option.value)
                        return (
                            <Group flex="1" gap="xs">
                                {country?.iso_code_2 && (
                                    <Avatar src={`https://flagcdn.com/w40/${country.iso_code_2.toLowerCase()}.png`} alt={country.iso_code_2} size="sm" radius="xl" />
                                )}
                                <Text size="sm">{option.label}</Text>
                                {checked && <IconCheck style={{ marginInlineStart: 'auto' }} size={16} />}
                            </Group>
                        )
                    }}
                    error={errors.country_id}
                    className="mb-3"
                />

                <Select
                    label="Native Language"
                    placeholder="Select Country"
                    data={countryList || []}
                    value={form.native_language_country_id}
                    onChange={(val) => handleChange('native_language_country_id', val)}
                    disabled={loadingCountries}
                    searchable
                    withAsterisk
                    leftSection={
                        (() => {
                            const selected = (countryList || []).find(c => c.value === form.native_language_country_id)
                            return selected?.iso_code_2 ? (
                                <Avatar src={`https://flagcdn.com/w40/${selected.iso_code_2.toLowerCase()}.png`} size="xs" radius="xl" />
                            ) : null
                        })()
                    }
                    renderOption={({ option, checked }) => {
                        const country = countryList.find(c => c.value === option.value)
                        return (
                            <Group flex="1" gap="xs">
                                {country?.iso_code_2 && (
                                    <Avatar src={`https://flagcdn.com/w40/${country.iso_code_2.toLowerCase()}.png`} alt={country.iso_code_2} size="sm" radius="xl" />
                                )}
                                <Text size="sm">{option.label}</Text>
                                {checked && <IconCheck style={{ marginInlineStart: 'auto' }} size={16} />}
                            </Group>
                        )
                    }}
                    error={errors.native_language}
                    className="mb-3"
                />

                {/* <TextInput
                    label="Native Language"
                    placeholder="e.g. AL"
                    value={form.native_language}
                    onChange={(e) => handleChange('native_language', e.target.value)}
                    className="mb-3"
                /> */}

                <TextInput
                    label="HS Code"
                    placeholder="Enter Code"
                    value={form.code}
                    onChange={(e) => handleChange('code', e.target.value)}
                    withAsterisk
                    error={errors.code}
                    className="mb-3"
                />

                <TextInput
                    label="Description"
                    placeholder="Enter description"
                    value={form.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="mb-3"
                />

                <TextInput
                    label="Native Description"
                    placeholder="Enter native description"
                    value={form.native_description}
                    onChange={(e) => handleChange('native_description', e.target.value)}
                    className="mb-3"
                />

                <Divider my="sm" label="Taxes" labelPosition="center" />

                <Grid>
                    <Grid.Col span={4}>
                        <NumberInput
                            label="BM"
                            suffix=" %"
                            value={form.bm}
                            onChange={(val) => handleChange('bm', val)}
                            min={0}
                        />
                    </Grid.Col>
                    <Grid.Col span={4}>
                        <NumberInput
                            label="PPN"
                            suffix=" %"
                            value={form.ppn}
                            onChange={(val) => handleChange('ppn', val)}
                            min={0}
                        />
                    </Grid.Col>
                    <Grid.Col span={4}>
                        <NumberInput
                            label="PPH"
                            suffix=" %"
                            value={form.pph}
                            onChange={(val) => handleChange('pph', val)}
                            min={0}
                        />
                    </Grid.Col>
                </Grid>
                <Text size="sm" mt="xs" ta="right" fw={500}>
                    Total Tax: <Text span c="red" fw={700}>{(Number(form.bm) + Number(form.ppn) + Number(form.pph))}%</Text>
                </Text>

                <Divider my="sm" label="Rules" labelPosition="center" />

                <MultiSelect
                    label={`Category Rules ${form.rule_ids.length > 0 ? `(${form.rule_ids.length})` : ''}`}
                    placeholder={loadingRules ? "Loading..." : "Select Rules"}
                    data={ruleList}
                    value={form.rule_ids}
                    onChange={(val) => handleChange('rule_ids', val)}
                    searchable
                    hidePickedOptions
                />

            </Box>

            <Group justify="flex-end" mt="xl">
                <Button variant="default" onClick={onCloseModal}>Cancel</Button>
                <Button onClick={handleSubmit} color="red">
                    {dataDetail ? "Update" : "Submit"}
                </Button>
            </Group>
        </Box>
    )
}

export default FormHscode
